import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, Eye, EyeOff, KeyRound, Pencil, Plus, Search, ShieldCheck, Trash2, X } from 'lucide-react';
import { usersAPI, storesAPI } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import FilterSelect from '../../components/common/FilterSelect';
import ConfirmModal from '../../components/common/ConfirmModal';
import Skeleton, { SkeletonText } from '../../components/common/Skeleton';
import TopProgressBar from '../../components/common/TopProgressBar';

const ALL_STORE_VALUE = 'all';
const USERS_LIST_QUERY = { page: 1, limit: 5000 };

const FEATURE_OPTIONS = [
  { key: 'tasks.board', label: 'Tasks Board' },
  { key: 'logistics.tracker', label: 'Logistic Report' },
  { key: 'logistics.manage', label: 'Logistic Desk' },
  { key: 'logistics.desk', label: 'Logistic Desk (Legacy Alias)' },
  { key: 'dairy_order.task', label: 'Dairy Order Task' },
  { key: 'dairy_order.report', label: 'Dairy Order Report' },
  { key: 'goods_received.task', label: 'Goods Received Task' },
  { key: 'goods_received.report', label: 'Goods Received Report' },
  { key: 'product_returns.task', label: 'Product Returns Task' },
  { key: 'product_returns.report', label: 'Product Returns Report' },
  { key: 'item_change_log.page', label: 'Item Change Log' },
  { key: 'produce_tracker.task', label: 'Produce Tracker Task' },
  { key: 'produce_tracker.report', label: 'Produce Tracker Report' },
  { key: 'inventory_count.task', label: 'Inventory Count Task' },
  { key: 'inventory_count.report', label: 'Inventory Count Report' },
  { key: 'daily_liquor_count.task', label: 'Daily Liquor Count Task' },
  { key: 'daily_liquor_count.report', label: 'Daily Liquor Count Report' },
  { key: 'final_report.view', label: 'Final Report' },
  { key: 'inventory_count.manage', label: 'Inventory Count Schedule' },
  { key: 'workforce_roster.view', label: 'Roster View' },
  { key: 'workforce_roster.manage', label: 'Roster Manage' },
  { key: 'reports.hub', label: 'Reports Hub' },
  { key: 'reports.all_stores', label: 'Reports All Stores' },
  { key: 'inventory.daily', label: 'Daily Inventory' },
  { key: 'products.sync', label: 'Product Snapshot' },
  { key: 'stores.manage', label: 'Store Management' },
  { key: 'dashboard.admin_overview', label: 'Dashboard Admin Overview' },
  { key: 'notifications.admin', label: 'Notice Board Admin' },
  { key: 'users.manage', label: 'User Management' },
  { key: 'admin.feature_access', label: 'Feature Access Admin' },
  { key: 'admin.category_classifications', label: 'Category Classification Admin' },
  { key: 'admin.assets', label: 'Store Assets Admin' },
];

const DEFAULT_METRICS = {
  tasksCompleted: 0,
  tasksOverdue: 0,
  averageCompletionTime: 0,
  rating: 0,
  requiresPasswordReset: false,
};

const normalizeStorePayloadValue = (value) => {
  if (!value || value === ALL_STORE_VALUE) return null;
  return value;
};

const getStoreOptionValue = (store = {}) => (
  store.storeInfoId
  || store.store_info_id
  || store.id
);

const getRoleLabel = (user) => user?.roles?.name || user?.role || user?.role_name || user?.role_id || '';

const getFeatureConfig = (user) => {
  const metrics = user?.performance_metrics || user?.performanceMetrics || {};
  const config = metrics.featureAccess || metrics.feature_access || user?.featureAccess || user?.feature_access;

  if (!config) return { mode: 'inherit', actions: [] };
  if (Array.isArray(config)) return { mode: 'custom', actions: config };
  if (typeof config === 'object') {
    return {
      mode: String(config.mode || '').toLowerCase() === 'inherit' ? 'inherit' : 'custom',
      actions: Array.isArray(config.actions) ? config.actions : [],
    };
  }
  return { mode: 'inherit', actions: [] };
};

const normalizeActionList = (actions) => (
  Array.from(new Set((actions || []).map((action) => String(action).trim()).filter(Boolean)))
);

const getRoleFeatureActions = (role) => {
  const permissions = Array.isArray(role?.permissions) ? role.permissions : [];
  const featurePermission = permissions.find(
    (entry) => String(entry?.module || '').trim().toLowerCase() === 'feature_access'
  );
  return normalizeActionList(Array.isArray(featurePermission?.actions) ? featurePermission.actions : []);
};

const sameActionSet = (left, right) => {
  const leftActions = normalizeActionList(left).sort();
  const rightActions = normalizeActionList(right).sort();
  if (leftActions.length !== rightActions.length) return false;
  return leftActions.every((action, index) => action === rightActions[index]);
};

const buildMetrics = (baseMetrics, featureMode, featureActions) => {
  const nextMetrics = {
    ...DEFAULT_METRICS,
    ...(baseMetrics && typeof baseMetrics === 'object' && !Array.isArray(baseMetrics) ? baseMetrics : {}),
  };

  delete nextMetrics.roleLabel;
  delete nextMetrics.role_label;

  if (featureMode === 'inherit') {
    delete nextMetrics.featureAccess;
    delete nextMetrics.feature_access;
    return nextMetrics;
  }

  const normalizedActions = normalizeActionList(featureActions);
  const featureAccess = { mode: 'custom', actions: normalizedActions };
  nextMetrics.featureAccess = featureAccess;
  nextMetrics.feature_access = featureAccess;
  return nextMetrics;
};

const getApiErrorMessage = (error, fallbackMessage) =>
  error?.response?.data?.errors?.[0]?.message ||
  error?.response?.data?.message ||
  fallbackMessage;

const UserCardSkeleton = () => (
  <div className="rounded-md border border-gray-200 bg-white p-4" aria-hidden="true">
    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <Skeleton className="h-5 w-44" />
          <Skeleton className="h-5 w-16 rounded-full" />
        </div>
        <SkeletonText className="mt-3 w-64 max-w-full" />
        <div className="mt-4 flex flex-wrap gap-2">
          <Skeleton className="h-7 w-20" />
          <Skeleton className="h-7 w-28" />
          <Skeleton className="h-7 w-24" />
        </div>
      </div>
      <div className="flex shrink-0 gap-2">
        <Skeleton className="h-10 w-20" />
        <Skeleton className="h-10 w-24" />
      </div>
    </div>
  </div>
);

const CandidateCardSkeleton = () => (
  <div className="rounded-md border border-gray-200 bg-white p-3" aria-hidden="true">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <Skeleton className="h-4 w-44 max-w-full" />
        <SkeletonText className="mt-3 w-64 max-w-full" />
        <SkeletonText className="mt-2 w-28" />
      </div>
      <Skeleton className="h-5 w-16 rounded-full" />
    </div>
  </div>
);

const UserManagement = () => {
  const { hasFeatureAccess } = useAuthStore();
  const canManageUsers = hasFeatureAccess('users.manage');

  const [loading, setLoading] = useState(false);
  const [candidateLoading, setCandidateLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [stores, setStores] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState('assign');
  const [selectedUser, setSelectedUser] = useState(null);
  const [candidateSearch, setCandidateSearch] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [isCandidateSearchMode, setIsCandidateSearchMode] = useState(false);
  const [newDesignationMode, setNewDesignationMode] = useState(false);
  const [newDesignationValue, setNewDesignationValue] = useState('');
  const [featureMode, setFeatureMode] = useState('inherit');
  const [featureActions, setFeatureActions] = useState([]);
  const [userPendingRemoval, setUserPendingRemoval] = useState(null);
  const [isPasswordResetMode, setIsPasswordResetMode] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswordFields, setShowPasswordFields] = useState({
    newPassword: false,
    confirmPassword: false,
  });

  const [form, setForm] = useState({
    designation: '',
    storeId: ALL_STORE_VALUE,
    roleId: '',
    isActive: true,
  });

  const roleSelectOptions = useMemo(() => ([
    { value: '', label: 'Select role...' },
    ...roles.map((role) => ({ value: String(role.id), label: role.name })),
  ]), [roles]);

  const storeSelectOptions = useMemo(() => ([
    { value: ALL_STORE_VALUE, label: 'All Store' },
    ...stores.map((store) => ({ value: String(getStoreOptionValue(store)), label: store.name })),
  ]), [stores]);

  const designationOptions = useMemo(() => {
    const set = new Set(designations || []);
    if (form.designation) set.add(form.designation);
    return Array.from(set);
  }, [designations, form.designation]);

  const designationSelectOptions = useMemo(() => ([
    { value: '', label: 'Select designation...' },
    ...designationOptions.map((designation) => ({ value: designation, label: designation })),
    { value: '__add_new__', label: '+ Add new designation' },
  ]), [designationOptions]);

  const getRoleDefaultActions = (roleId) => {
    const role = roles.find((entry) => String(entry.id) === String(roleId));
    return getRoleFeatureActions(role);
  };

  const filteredUsers = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return users;

    const includes = (value) => String(value || '').toLowerCase().includes(q);
    return users.filter((entry) => (
      includes(entry.name)
      || includes(entry.email)
      || includes(entry.designation)
      || includes(getRoleLabel(entry))
      || includes(entry.stores?.name)
      || includes(entry.store_name)
    ));
  }, [users, searchQuery]);

  const selectedIdentity = drawerMode === 'edit' ? selectedUser : selectedCandidate;
  const isAssignSearchMode = drawerMode === 'assign' && isCandidateSearchMode;
  const isResetPasswordView = drawerMode === 'edit' && isPasswordResetMode;
  const isInitialUsersLoading = loading && users.length === 0;
  const isRefreshingUsers = loading && users.length > 0;
  const isPageBusy = loading || candidateLoading;
  const featureActionsGrantAll = featureActions.includes('all') || featureActions.includes('*');

  const loadAll = async () => {
    setLoading(true);
    try {
      const [rolesRes, usersRes, storesRes, designationsRes] = await Promise.all([
        usersAPI.getRoles(),
        usersAPI.getAll(USERS_LIST_QUERY),
        storesAPI.getAll(),
        usersAPI.getDesignations(),
      ]);

      setRoles(rolesRes.data?.data || []);
      setUsers(usersRes.data?.data || []);
      setStores(storesRes.data?.data || []);
      setDesignations(designationsRes.data?.data || []);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load user management data'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!canManageUsers) return;
    loadAll();
  }, [canManageUsers]);

  const resetDrawer = () => {
    setDrawerOpen(false);
    setDrawerMode('assign');
    setSelectedUser(null);
    setSelectedCandidate(null);
    setCandidateSearch('');
    setCandidates([]);
    setIsCandidateSearchMode(false);
    setNewDesignationMode(false);
    setNewDesignationValue('');
    setFeatureMode('inherit');
    setFeatureActions([]);
    setIsPasswordResetMode(false);
    setPasswordForm({
      newPassword: '',
      confirmPassword: '',
    });
    setShowPasswordFields({
      newPassword: false,
      confirmPassword: false,
    });
    setForm({
      designation: '',
      storeId: ALL_STORE_VALUE,
      roleId: '',
      isActive: true,
    });
  };

  const openAssignDrawer = () => {
    resetDrawer();
    setDrawerMode('assign');
    setIsCandidateSearchMode(true);
    setDrawerOpen(true);
  };

  const openEditDrawer = (user) => {
    const featureConfig = getFeatureConfig(user);
    setDrawerMode('edit');
    setSelectedUser(user);
    setSelectedCandidate(null);
    setCandidates([]);
    setCandidateSearch('');
    setIsCandidateSearchMode(false);
    setNewDesignationMode(false);
    setFeatureMode(featureConfig.mode);
    setFeatureActions(featureConfig.actions);
    setIsPasswordResetMode(false);
    setPasswordForm({
      newPassword: '',
      confirmPassword: '',
    });
    setShowPasswordFields({
      newPassword: false,
      confirmPassword: false,
    });
    setForm({
      designation: user.designation || '',
      storeId: user.storeInfoId || user.store_info_id || user.store_id || user.storeId || ALL_STORE_VALUE,
      roleId: user.role_id || user.roleId || '',
      isActive: user.is_active !== false,
    });
    setDrawerOpen(true);
  };

  const openCandidateSearch = () => {
    setCandidateSearch(selectedCandidate?.email || '');
    setCandidates([]);
    setIsCandidateSearchMode(true);
  };

  const cancelCandidateSearch = () => {
    setIsCandidateSearchMode(false);
    setCandidates([]);
  };

  const selectCandidate = (candidate) => {
    setSelectedCandidate(candidate);
    setCandidateSearch(candidate.email || candidate.name || '');
    setIsCandidateSearchMode(false);
    setCandidates([]);
  };

  const searchCandidates = async () => {
    const query = candidateSearch.trim();
    if (query.length < 2) {
      toast.error('Enter at least 2 characters');
      return;
    }

    setCandidateLoading(true);
    try {
      const response = await usersAPI.searchCandidates({ search: query, limit: 20 });
      setCandidates(response.data?.data || []);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to search users'));
    } finally {
      setCandidateLoading(false);
    }
  };

  const toggleFeatureAction = (action) => {
    setFeatureActions((current) => {
      const next = new Set(current);
      if (next.has(action)) next.delete(action);
      else next.add(action);
      return Array.from(next);
    });
  };

  const changeFeatureMode = (nextMode) => {
    setFeatureMode(nextMode);
    if (nextMode !== 'custom') return;

    setFeatureActions((current) => (
      normalizeActionList(current).length > 0
        ? current
        : getRoleDefaultActions(form.roleId)
    ));
  };

  const changeRole = (nextValue) => {
    const nextRoleId = String(nextValue);
    const previousRoleId = form.roleId;
    const previousRoleDefaults = getRoleDefaultActions(previousRoleId);
    const nextRoleDefaults = getRoleDefaultActions(nextRoleId);

    setForm((current) => ({ ...current, roleId: nextRoleId }));

    if (featureMode === 'custom') {
      setFeatureActions((current) => {
        const normalizedCurrent = normalizeActionList(current);
        if (normalizedCurrent.length === 0 || sameActionSet(normalizedCurrent, previousRoleDefaults)) {
          return nextRoleDefaults;
        }
        return current;
      });
    }
  };

  const openPasswordResetMode = () => {
    setPasswordForm({
      newPassword: '',
      confirmPassword: '',
    });
    setShowPasswordFields({
      newPassword: false,
      confirmPassword: false,
    });
    setIsPasswordResetMode(true);
  };

  const closePasswordResetMode = () => {
    if (loading) return;
    setPasswordForm({
      newPassword: '',
      confirmPassword: '',
    });
    setShowPasswordFields({
      newPassword: false,
      confirmPassword: false,
    });
    setIsPasswordResetMode(false);
  };

  const submitPasswordReset = async () => {
    if (!selectedUser) return;

    const newPassword = passwordForm.newPassword.trim();
    const confirmPassword = passwordForm.confirmPassword.trim();

    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Password and confirm password do not match');
      return;
    }

    setLoading(true);
    try {
      await usersAPI.updatePassword(selectedUser.id, {
        newPassword,
        confirmPassword,
      });
      toast.success('Password reset successfully');
      await loadAll();
      resetDrawer();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to reset password'));
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordField = (field) => {
    setShowPasswordFields((current) => ({ ...current, [field]: !current[field] }));
  };

  const submitDrawer = async () => {
    if (!form.roleId) {
      toast.error('Role is required');
      return;
    }
    if (!form.designation.trim()) {
      toast.error('Designation is required');
      return;
    }
    if (drawerMode === 'assign' && !selectedCandidate) {
      toast.error('Select an existing Foodmandu user');
      return;
    }
    if (drawerMode === 'assign' && selectedCandidate?.isAssigned) {
      toast.error('This user is already assigned to OneMart');
      return;
    }

    const baseMetrics = drawerMode === 'edit'
      ? selectedUser?.performance_metrics || selectedUser?.performanceMetrics
      : DEFAULT_METRICS;
    const performanceMetrics = buildMetrics(baseMetrics, featureMode, featureActions);
    const payload = {
      roleId: form.roleId,
      storeId: normalizeStorePayloadValue(form.storeId),
      storeInfoId: normalizeStorePayloadValue(form.storeId),
      designation: form.designation.trim(),
      isActive: Boolean(form.isActive),
      performanceMetrics,
    };

    setLoading(true);
    try {
      if (drawerMode === 'assign') {
        await usersAPI.create({
          ...payload,
          aspnetUserId: selectedCandidate.aspnetUserId || selectedCandidate.aspnet_user_id || selectedCandidate.id,
        });
        toast.success('OneMart access assigned');
      } else {
        await usersAPI.update(selectedUser.id, payload);
        toast.success('OneMart access updated');
      }

      await loadAll();
      resetDrawer();
    } catch (error) {
      toast.error(getApiErrorMessage(error, drawerMode === 'assign' ? 'Failed to assign user' : 'Failed to update user'));
    } finally {
      setLoading(false);
    }
  };

  const closeRemoveConfirm = () => {
    if (loading) return;
    setUserPendingRemoval(null);
  };

  const deactivateUser = async () => {
    if (!userPendingRemoval) return;

    setLoading(true);
    try {
      await usersAPI.delete(userPendingRemoval.id);
      toast.success('OneMart access removed');
      await loadAll();
      if (selectedUser?.id === userPendingRemoval.id) resetDrawer();
      setUserPendingRemoval(null);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to remove OneMart access'));
    } finally {
      setLoading(false);
    }
  };

  if (!canManageUsers) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">OneMart Users</h1>
        <div className="card">
          <p className="text-gray-500">Access denied.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <TopProgressBar active={isPageBusy} />

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">OneMart Users</h1>
          <p className="mt-1 text-sm text-gray-500">Manage OneMart access for existing Foodmandu accounts.</p>
        </div>
        <button
          type="button"
          className="btn-primary inline-flex items-center justify-center gap-2"
          onClick={openAssignDrawer}
          disabled={loading}
        >
          <Plus className="h-4 w-4" />
          Assign User
        </button>
      </div>

      <div className="card min-h-[220px]" aria-busy={loading}>
        <div className="mb-4 grid gap-3 md:grid-cols-[1fr_auto] md:items-center">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              className="input pl-9"
              placeholder="Search name, email, role, designation, store"
              disabled={loading}
            />
          </div>
          <span className="text-sm text-gray-500">
            {isInitialUsersLoading
              ? 'Loading users...'
              : isRefreshingUsers ? 'Refreshing users...' : `${filteredUsers.length} assigned`}
          </span>
        </div>

        {isInitialUsersLoading ? (
          <div className="grid gap-3 xl:grid-cols-2">
            {Array.from({ length: 6 }).map((_, index) => (
              <UserCardSkeleton key={index} />
            ))}
          </div>
        ) : filteredUsers.length === 0 ? (
          <p className="text-gray-500">No OneMart users found.</p>
        ) : (
          <div className="grid gap-3 xl:grid-cols-2">
            {filteredUsers.map((entry) => (
              <div key={entry.id} className="rounded-md border border-gray-200 bg-white p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="truncate text-base font-semibold text-gray-900">{entry.name}</h2>
                      <span className={`badge ${entry.is_active === false ? 'badge-warning' : 'badge-success'}`}>
                        {entry.is_active === false ? 'Inactive' : 'Active'}
                      </span>
                    </div>
                    <p className="mt-1 truncate text-sm text-gray-500">{entry.email}</p>
                    <div className="mt-3 flex flex-wrap gap-2 text-xs text-gray-600">
                      <span className="rounded-md bg-gray-100 px-2 py-1">{getRoleLabel(entry) || 'No role'}</span>
                      <span className="rounded-md bg-gray-100 px-2 py-1">{entry.designation || 'No designation'}</span>
                      <span className="rounded-md bg-gray-100 px-2 py-1">{entry.stores?.name || entry.store_name || 'All Store'}</span>
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <button
                      type="button"
                      className="btn-secondary inline-flex items-center gap-2 !px-3 !py-2 text-sm"
                      onClick={() => openEditDrawer(entry)}
                      disabled={loading}
                    >
                      <Pencil className="h-4 w-4" />
                      Edit
                    </button>
                    <button
                      type="button"
                      className="btn-danger inline-flex items-center gap-2 !px-3 !py-2 text-sm"
                      onClick={() => setUserPendingRemoval(entry)}
                      disabled={loading}
                    >
                      <Trash2 className="h-4 w-4" />
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {drawerOpen && createPortal((
        <div className="fixed bottom-0 left-0 right-0 top-0 z-[80]">
          <div className="absolute inset-0 bg-slate-950/40" onClick={loading ? undefined : resetDrawer} />
          <div className="absolute bottom-0 right-0 top-0 flex w-full justify-end">
            <div className="relative flex h-full min-h-0 w-full flex-col overflow-hidden bg-white shadow-2xl sm:max-w-xl md:max-w-2xl xl:max-w-3xl">
              <div className="shrink-0 border-b border-gray-200 bg-white px-4 py-4 sm:px-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">
                      {isResetPasswordView
                        ? 'Reset Password'
                        : isAssignSearchMode
                        ? 'Search Foodmandu User'
                        : drawerMode === 'assign' ? 'Assign OneMart User' : 'Edit OneMart Access'}
                    </h2>
                    <p className="mt-1 text-sm text-gray-500">
                      {isResetPasswordView
                        ? selectedUser?.email
                        : isAssignSearchMode
                        ? 'Find an existing account, then select it to continue.'
                        : drawerMode === 'assign' ? 'Select an existing Foodmandu account.' : selectedUser?.email}
                    </p>
                  </div>
                  <button type="button" className="btn-secondary !p-2" onClick={resetDrawer} disabled={loading}>
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-4 py-5 sm:px-6">
                {isResetPasswordView ? (
                  <section className="mx-auto w-full max-w-lg space-y-5">
                    <div className="rounded-md border border-gray-200 bg-gray-50 p-4">
                      <div className="flex items-start gap-3">
                        <KeyRound className="mt-1 h-5 w-5 shrink-0 text-primary-600" />
                        <div className="min-w-0">
                          <h3 className="text-sm font-semibold text-gray-900">Set New Password</h3>
                          <p className="mt-1 text-sm text-gray-500">
                            Enter the new password for {selectedUser?.name || selectedUser?.email}.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="label" htmlFor="adminNewPassword">New Password</label>
                      <div className="relative">
                        <input
                          id="adminNewPassword"
                          type={showPasswordFields.newPassword ? 'text' : 'password'}
                          className="input pr-11"
                          value={passwordForm.newPassword}
                          onChange={(event) => setPasswordForm((current) => ({ ...current, newPassword: event.target.value }))}
                          autoComplete="new-password"
                          disabled={loading}
                        />
                        <button
                          type="button"
                          className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                          onClick={() => togglePasswordField('newPassword')}
                          disabled={loading}
                          aria-label={showPasswordFields.newPassword ? 'Hide new password' : 'Show new password'}
                        >
                          {showPasswordFields.newPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="label" htmlFor="adminConfirmPassword">Confirm Password</label>
                      <div className="relative">
                        <input
                          id="adminConfirmPassword"
                          type={showPasswordFields.confirmPassword ? 'text' : 'password'}
                          className="input pr-11"
                          value={passwordForm.confirmPassword}
                          onChange={(event) => setPasswordForm((current) => ({ ...current, confirmPassword: event.target.value }))}
                          autoComplete="new-password"
                          disabled={loading}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') submitPasswordReset();
                          }}
                        />
                        <button
                          type="button"
                          className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                          onClick={() => togglePasswordField('confirmPassword')}
                          disabled={loading}
                          aria-label={showPasswordFields.confirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                        >
                          {showPasswordFields.confirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                  </section>
                ) : isAssignSearchMode ? (
                  <section className="min-h-[220px] space-y-4 rounded-md" aria-busy={candidateLoading}>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900">Foodmandu Account</h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Search by email, username, phone, or name. Selecting a user returns you to the access form.
                      </p>
                    </div>

                    <div className="flex flex-col gap-2 sm:flex-row">
                      <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                        <input
                          className="input pl-9"
                          value={candidateSearch}
                          autoFocus
                          onChange={(event) => setCandidateSearch(event.target.value)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') searchCandidates();
                          }}
                          placeholder="Search existing email or name"
                          disabled={candidateLoading || loading}
                        />
                      </div>
                      <button
                        type="button"
                        className="btn-secondary inline-flex items-center justify-center gap-2"
                        onClick={searchCandidates}
                        disabled={candidateLoading || loading}
                      >
                        <Search className="h-4 w-4" />
                        {candidateLoading ? 'Searching...' : 'Search'}
                      </button>
                    </div>

                    {candidateLoading ? (
                      <div className="grid gap-2">
                        {Array.from({ length: 4 }).map((_, index) => (
                          <CandidateCardSkeleton key={index} />
                        ))}
                      </div>
                    ) : candidates.length > 0 ? (
                      <div className="grid gap-2">
                        {candidates.map((candidate) => (
                          <button
                            key={candidate.aspnetUserId || candidate.id}
                            type="button"
                            className={`rounded-md border p-3 text-left transition ${
                              candidate.isAssigned
                                ? 'cursor-not-allowed border-gray-200 bg-gray-50 opacity-75'
                                : 'border-gray-200 bg-white hover:bg-gray-50'
                            }`}
                            onClick={() => {
                              if (!candidate.isAssigned) selectCandidate(candidate);
                            }}
                            disabled={candidateLoading || candidate.isAssigned}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <div className="font-medium text-gray-900">{candidate.name || candidate.email}</div>
                                <div className="truncate text-sm text-gray-500">{candidate.email}</div>
                                <div className="mt-1 text-xs text-gray-500">{candidate.phone || candidate.username || '-'}</div>
                              </div>
                              <div className="flex shrink-0 flex-col items-end gap-1">
                                {candidate.isAssigned ? (
                                  <span className="badge-warning badge">Assigned</span>
                                ) : (
                                  <span className="badge-info badge">Available</span>
                                )}
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </section>
                ) : (
                  <>
                    {selectedIdentity ? (
                      <section className="rounded-md border border-gray-200 bg-gray-50 p-4">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                          <div className="flex min-w-0 items-start gap-3">
                            <ShieldCheck className="mt-1 h-5 w-5 shrink-0 text-primary-600" />
                            <div className="min-w-0">
                              <div className="font-semibold text-gray-900">{selectedIdentity.name || selectedIdentity.email}</div>
                              <div className="truncate text-sm text-gray-500">{selectedIdentity.email}</div>
                            </div>
                          </div>
                          {drawerMode === 'assign' ? (
                            <button
                              type="button"
                              className="btn-secondary shrink-0 !px-3 !py-2 text-sm"
                              onClick={openCandidateSearch}
                              disabled={loading}
                            >
                              Change
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="btn-secondary inline-flex shrink-0 items-center gap-2 !px-3 !py-2 text-sm"
                              onClick={openPasswordResetMode}
                              disabled={loading}
                            >
                              <KeyRound className="h-4 w-4" />
                              Reset Password
                            </button>
                          )}
                        </div>
                      </section>
                    ) : drawerMode === 'assign' ? (
                      <section className="rounded-md border border-dashed border-gray-300 bg-gray-50 p-4">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                          <div>
                            <h3 className="text-sm font-semibold text-gray-900">No Foodmandu account selected</h3>
                            <p className="mt-1 text-sm text-gray-500">Search and select an existing account before assigning access.</p>
                          </div>
                          <button
                            type="button"
                            className="btn-secondary inline-flex items-center justify-center gap-2"
                            onClick={openCandidateSearch}
                            disabled={loading}
                          >
                            <Search className="h-4 w-4" />
                            Search User
                          </button>
                        </div>
                      </section>
                    ) : null}

                    <section className="grid gap-4 md:grid-cols-2">
                      <div>
                        <label className="label">Role</label>
                        <FilterSelect
                          className="w-full"
                          value={String(form.roleId || '')}
                          options={roleSelectOptions}
                          onChange={changeRole}
                          disabled={loading}
                        />
                      </div>
                      <div>
                        <label className="label">Store</label>
                        <FilterSelect
                          className="w-full"
                          value={String(form.storeId || ALL_STORE_VALUE)}
                          options={storeSelectOptions}
                          onChange={(nextValue) => setForm((current) => ({ ...current, storeId: String(nextValue) }))}
                          disabled={loading}
                        />
                      </div>
                      <div>
                        <label className="label">Designation</label>
                        {!newDesignationMode ? (
                          <FilterSelect
                            className="w-full"
                            value={form.designation || ''}
                            options={designationSelectOptions}
                            onChange={(nextValue) => {
                              const value = String(nextValue);
                              if (value === '__add_new__') {
                                setNewDesignationMode(true);
                                setNewDesignationValue('');
                              } else {
                                setForm((current) => ({ ...current, designation: value }));
                              }
                            }}
                            disabled={loading}
                          />
                        ) : (
                          <div className="space-y-2">
                            <input
                              className="input"
                              value={newDesignationValue}
                              onChange={(event) => setNewDesignationValue(event.target.value)}
                              placeholder="Designation"
                              disabled={loading}
                            />
                            <div className="flex gap-2">
                              <button
                                type="button"
                                className="btn-primary !py-2"
                                onClick={() => {
                                  const value = newDesignationValue.trim();
                                  if (!value) return;
                                  setDesignations((current) => Array.from(new Set([...(current || []), value])));
                                  setForm((current) => ({ ...current, designation: value }));
                                  setNewDesignationMode(false);
                                  setNewDesignationValue('');
                                }}
                                disabled={loading}
                              >
                                Add
                              </button>
                              <button
                                type="button"
                                className="btn-secondary !py-2"
                                onClick={() => setNewDesignationMode(false)}
                                disabled={loading}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                      <div>
                        <label className="label">Status</label>
                        <label className="flex min-h-[42px] items-center gap-3 rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-700">
                          <input
                            type="checkbox"
                            checked={Boolean(form.isActive)}
                            onChange={(event) => setForm((current) => ({ ...current, isActive: event.target.checked }))}
                            disabled={loading}
                          />
                          Active in OneMart
                        </label>
                      </div>
                    </section>

                    <section className="space-y-3">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <h3 className="text-sm font-semibold text-gray-900">Feature Access</h3>
                        <div className="flex rounded-md border border-gray-200 p-1">
                          {['inherit', 'custom'].map((mode) => (
                            <button
                              key={mode}
                              type="button"
                              className={`rounded px-3 py-1.5 text-sm font-medium ${
                                featureMode === mode ? 'bg-primary-600 text-white' : 'text-gray-600 hover:bg-gray-100'
                              }`}
                              onClick={() => changeFeatureMode(mode)}
                              disabled={loading}
                            >
                              {mode === 'inherit' ? 'Inherit Role' : 'Custom'}
                            </button>
                          ))}
                        </div>
                      </div>

                      {featureMode === 'custom' ? (
                        <div className="space-y-3">
                          {featureActionsGrantAll && (
                            <div className="rounded-md border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-800">
                              This custom access currently grants all available features.
                            </div>
                          )}
                          <div className="grid gap-2 sm:grid-cols-2">
                            {FEATURE_OPTIONS.map((feature) => {
                              const checked = featureActionsGrantAll || featureActions.includes(feature.key);
                              return (
                                <label
                                  key={feature.key}
                                  className={`flex items-start gap-3 rounded-md border p-3 ${
                                    checked ? 'border-primary-300 bg-primary-50' : 'border-gray-200 bg-white'
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    className="mt-1"
                                    checked={checked}
                                    onChange={() => toggleFeatureAction(feature.key)}
                                    disabled={loading || featureActionsGrantAll}
                                  />
                                  <span>
                                    <span className="block text-sm font-medium text-gray-900">{feature.label}</span>
                                    <span className="mt-1 block font-mono text-[11px] text-gray-500">{feature.key}</span>
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      ) : (
                        <div className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
                          This user will use the selected role's default feature access.
                        </div>
                      )}
                    </section>
                  </>
                )}
              </div>

              <div className="shrink-0 border-t border-gray-200 bg-white px-4 py-4 sm:px-6">
                {isResetPasswordView ? (
                  <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
                    <button
                      type="button"
                      className="btn-secondary inline-flex items-center justify-center gap-2"
                      onClick={closePasswordResetMode}
                      disabled={loading}
                    >
                      <ArrowLeft className="h-4 w-4" />
                      Back
                    </button>
                    <button
                      type="button"
                      className="btn-primary inline-flex items-center justify-center gap-2"
                      onClick={submitPasswordReset}
                      disabled={loading}
                    >
                      <KeyRound className="h-4 w-4" />
                      {loading ? 'Saving...' : 'Save Password'}
                    </button>
                  </div>
                ) : isAssignSearchMode ? (
                  <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                    <button type="button" className="btn-secondary" onClick={resetDrawer} disabled={loading}>
                      Close
                    </button>
                    <button type="button" className="btn-primary" onClick={cancelCandidateSearch} disabled={loading || candidateLoading}>
                      Cancel Search
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                    <button type="button" className="btn-secondary" onClick={resetDrawer} disabled={loading}>
                      Cancel
                    </button>
                    <button type="button" className="btn-primary" onClick={submitDrawer} disabled={loading || candidateLoading}>
                      {loading
                        ? (drawerMode === 'assign' ? 'Assigning...' : 'Saving...')
                        : drawerMode === 'assign' ? 'Assign Access' : 'Save Changes'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ), document.body)}

      <ConfirmModal
        isOpen={Boolean(userPendingRemoval)}
        title="Remove OneMart Access?"
        message={(
          <p>
            This will remove OneMart access for{' '}
            <span className="font-semibold text-gray-900">{userPendingRemoval?.name || userPendingRemoval?.email}</span>.
            The Foodmandu account itself will not be deleted.
          </p>
        )}
        confirmLabel="Remove Access"
        cancelLabel="Keep User"
        variant="danger"
        loading={loading}
        onClose={closeRemoveConfirm}
        onConfirm={deactivateUser}
      />
    </div>
  );
};

export default UserManagement;
